import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from 'react-query'
import isEmpty from 'lodash.isempty'
import { useTranslation } from 'react-i18next'

import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import { useFeatureTrackerOptional } from '@hapana/shared/web/hooks'
import { GENERIC_ERROR_KEY } from '@hapana/shared/constants/errors.constants'
import { useErrorHandler } from '@hapana/shared/web/hooks/errors.hooks'
import { useSiteId } from '@hapana/shared/web/hooks/site.hooks'
import useSiteStore from '_stores/site.store'
import {
  getSiteEnableTanningFeatures,
  getSiteTanningMaintenanceEmail,
} from '_services/tanning/sites.service'
import { getConfiguration } from '@hapana/shared/web/services/configuration.service'

import {
  getSites,
  getSite,
  getSiteInstructors,
  getSiteCustomProperties,
  getAvailableSites,
  getSiteById,
  updateSiteBusinessCustomization,
  updateSiteSettingsAccountBilling,
  updateSiteRegionalCommunication,
  toggleFrontDeskKioskBeta,
  updateSiteSettingsCheckIn,
  updateSiteSettingsSocialMedia,
  updateSiteSettingsAccountPointOfSale,
  getSiteEnableAddOns,
  getSiteTopazIntegration,
  getSiteEnableMembershipPlanChange,
  getSiteEnableDoorAccess,
  getSiteAdminSmsFromName,
  getSiteFobCredentialFormat,
  getSiteEnableSharedSites,
  getSiteConnectedSharedSiteIds,
} from '../services/sites.service'

export const useSites = () => {
  return useQuery({
    queryKey: [getSites.key],
    queryFn: getSites.query,
    select: (data) => data.sites.results.map(({ row }) => row),
  })
}

export const useAvailableSites = ({ paginate, search, filter, sort, enabled = true }) => {
  return useInfiniteQuery({
    queryKey: [
      getAvailableSites.key,
      {
        ...(!isEmpty(paginate) && { paginate }),
        ...(search && { search }),
        ...(!isEmpty(filter) && { filter }),
        ...(!isEmpty(sort) && { sort }),
      },
    ],
    queryFn: getAvailableSites.query,
    getNextPageParam: (lastPage) => {
      if (lastPage?.availableToAssignSites?.pageInfo?.hasNextPage) {
        const cursor =
          lastPage?.availableToAssignSites?.pageInfo?.endCursor ||
          lastPage?.availableToAssignSites?.results?.[
            lastPage.availableToAssignSites.results.length - 1
          ]?.cursor

        if (!cursor) {
          return undefined
        }

        return {
          paginate: {
            pageSize: paginate.pageSize,
            cursor,
          },
        }
      }
      return undefined
    },
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => ({
        availableToAssignSites: {
          ...page.availableToAssignSites,
          results: page.availableToAssignSites.results.map(({ node }) => node),
          pageInfo: {
            ...page.availableToAssignSites.pageInfo,
            endCursor:
              page.availableToAssignSites.pageInfo.endCursor ||
              page.availableToAssignSites.results[page.availableToAssignSites.results.length - 1]
                ?.cursor,
          },
        },
      })),
    }),
    enabled,
  })
}

export const useSiteInstructors = (siteIds) => {
  return useQuery({
    queryKey: [getSiteInstructors.key, { siteIds }],
    queryFn: getSiteInstructors.query,
    select: (data) => data.sitesCoreUserInstructors,
    enabled: !!siteIds && siteIds.length > 0,
  })
}

export const useSiteCustomProperties = (siteId) => {
  return useQuery({
    queryKey: [getSiteCustomProperties.key, { siteId }],
    queryFn: getSiteCustomProperties.query,
    select: (data) => data.siteCustomProperties,
    enabled: !!siteId,
  })
}

export const useSite = (siteId) => {
  return useQuery({
    queryKey: [getSite.key, { siteId }],
    queryFn: getSite.query,
    select: (data) => data.site,
    enabled: !!siteId,
  })
}

export const useGetSiteById = (siteId, options = {}) => {
  const { enabled, ...restOptions } = options
  return useQuery({
    queryKey: [getSiteById.key, { siteId }],
    queryFn: getSiteById.query,
    select: (data) => data.site,
    enabled: !!siteId && (enabled ?? true),
    ...restOptions,
  })
}

export const useUpdateSiteBusinessCustomization = (options) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const { closeDialog } = useDialog()
  const queryClient = useQueryClient()
  const featureTracker = useFeatureTrackerOptional('settings.location.general')

  const onSuccess = (data, variables) => {
    openToast({
      title: t(
        'settings.systemSettings.locationSettings.businessCustomization.toasts.businessCustomizationUpdated',
        'Business customization updated successfully'
      ),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
    })

    // Track audit event
    if (featureTracker) {
      const { onSuccess: auditOnSuccess } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        resourceId: variables?.siteId,
        settingName: 'site.businessCustomization',
        settingDisplayName: 'Business Customization',
        getOldValue: () => {
          const cached = queryClient.getQueryData([getSiteById.key, { siteId: variables?.siteId }])
          const site = cached?.site
          if (!site) {
            return undefined
          }
          return JSON.stringify({
            name: site?.settings?.location?.businessInformation?.name,
            description: site?.settings?.location?.businessInformation?.description,
            phone: site?.settings?.location?.businessInformation?.phone,
            dashboardRevenuePeriod:
              site?.settings?.location?.configurations?.dashboardRevenuePeriod,
          })
        },
        getNewValue: () => {
          return JSON.stringify(variables?.input)
        },
        getSummary: () => `Business customization settings updated for site ${variables?.siteId}`,
      })
      auditOnSuccess(data)
    }

    options?.onSuccess?.(data)
    closeDialog()
    queryClient.invalidateQueries([getSites.key])
    queryClient.invalidateQueries([getSiteById.key])
    queryClient.invalidateQueries(['gqty-site-by-id'])
  }

  const onError = (error) => {
    // Track audit error
    if (featureTracker) {
      const { onError: auditOnError } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        settingName: 'site.businessCustomization',
        settingDisplayName: 'Business Customization',
      })
      auditOnError(error)
    }

    openToast({
      title: t(
        'settings.systemSettings.locationSettings.businessCustomization.toasts.updateFailed',
        'Update failed'
      ),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
    })
    options?.onError?.(error)
  }

  return useMutation(updateSiteBusinessCustomization, {
    onSuccess,
    onError,
    ...options,
  })
}

export const useUpdateSiteSettingsAccountBilling = (options) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const { closeDialog } = useDialog()
  const queryClient = useQueryClient()
  const featureTracker = useFeatureTrackerOptional('settings.account.billing')

  const onSuccess = (data, variables) => {
    openToast({
      title: t(
        'core:settings.account.billing.updateSuccess',
        'Billing settings updated successfully'
      ),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
    })

    // Track audit event
    if (featureTracker) {
      const { onSuccess: auditOnSuccess } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        resourceId: variables?.siteId,
        settingName: 'site.accountBilling',
        settingDisplayName: 'Account Billing',
        getOldValue: () => {
          const cached = queryClient.getQueryData([getSiteById.key, { siteId: variables?.siteId }])
          const billing = cached?.site?.settings?.account?.billing
          if (!billing) {
            return undefined
          }
          return JSON.stringify({
            retryFailedPayments: billing.retryFailedPayments,
            transactionFeePreference: billing.transactionFeePreference,
            enabled: billing.enabled,
          })
        },
        getNewValue: () => {
          return JSON.stringify(variables?.input)
        },
        getSummary: () => `Billing settings updated for site ${variables?.siteId}`,
      })
      auditOnSuccess(data)
    }

    options?.onSuccess?.(data)
    closeDialog()
    queryClient.invalidateQueries([getSites.key])
    queryClient.invalidateQueries([getSiteById.key])
    queryClient.invalidateQueries(['gqty-site-by-id'])
    // Invalidate all site queries to ensure data refresh
    queryClient.invalidateQueries([getSiteById.key])
    queryClient.invalidateQueries(['gqty-site-by-id'])
  }

  const onError = (error) => {
    // Track audit error
    if (featureTracker) {
      const { onError: auditOnError } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        settingName: 'site.accountBilling',
        settingDisplayName: 'Account Billing',
      })
      auditOnError(error)
    }

    openToast({
      title: t('core:settings.account.billing.updateError', 'Failed to update billing settings'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
    })
    options?.onError?.(error)
  }

  return useMutation(updateSiteSettingsAccountBilling, {
    onSuccess,
    onError,
    ...options,
  })
}

export const useUpdateSiteRegionalCommunication = (options) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const { closeDialog } = useDialog()
  const queryClient = useQueryClient()
  const featureTracker = useFeatureTrackerOptional('settings.location.general')

  const onSuccess = (data, variables) => {
    openToast({
      title: t(
        'settings.systemSettings.locationSettings.regionalCommunication.toasts.regionalCommunicationUpdated',
        'Regional communication updated successfully'
      ),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
    })

    // Track audit event
    if (featureTracker) {
      const { onSuccess: auditOnSuccess } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        resourceId: variables?.siteId,
        settingName: 'site.regionalCommunication',
        settingDisplayName: 'Regional Communication',
        getOldValue: () => {
          const cached = queryClient.getQueryData([getSiteById.key, { siteId: variables?.siteId }])
          const config = cached?.site?.settings?.location?.configurations
          if (!config) {
            return undefined
          }
          return JSON.stringify({
            currency: config.currency,
            dateFormat: config.dateFormat,
            numberFormat: config.numberFormat,
            timezone: config.timezone,
            timeFormat: config.timeFormat,
          })
        },
        getNewValue: () => {
          return JSON.stringify(variables?.input)
        },
        getSummary: () => `Regional communication settings updated for site ${variables?.siteId}`,
      })
      auditOnSuccess(data)
    }

    options?.onSuccess?.(data)
    closeDialog()
    queryClient.invalidateQueries([getSites.key])
    queryClient.invalidateQueries([getSiteById.key])
    queryClient.invalidateQueries(['gqty-site-by-id'])
  }

  const onError = (error) => {
    // Track audit error
    if (featureTracker) {
      const { onError: auditOnError } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        settingName: 'site.regionalCommunication',
        settingDisplayName: 'Regional Communication',
      })
      auditOnError(error)
    }

    openToast({
      title: t(
        'settings.systemSettings.locationSettings.regionalCommunication.toasts.updateFailed',
        'Update failed'
      ),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
    })
    options?.onError?.(error)
  }

  return useMutation(updateSiteRegionalCommunication, {
    onSuccess,
    onError,
    ...options,
  })
}

export const useToggleFrontDeskKioskBeta = (options) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const queryClient = useQueryClient()
  const featureTracker = useFeatureTrackerOptional('settings.tools')

  const onSuccess = (data, variables) => {
    openToast({
      title: t('settings.locationTools.toasts.betaToggled', 'Beta version updated'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
    })

    // Track audit event
    if (featureTracker) {
      const { onSuccess: auditOnSuccess } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        resourceId: variables?.siteId,
        settingName: 'site.frontDeskKioskBeta',
        settingDisplayName: 'Front Desk Kiosk Beta',
        getOldValue: () => {
          const cached = queryClient.getQueryData([getSiteById.key, { siteId: variables?.siteId }])
          const betaEnabled =
            cached?.site?.settings?.location?.tools?.frontDeskKiosk?.betaVersionEnabled
          return betaEnabled != null ? String(betaEnabled) : undefined
        },
        getNewValue: () => String(variables?.enabled),
        getSummary: () =>
          `Front desk kiosk beta ${variables?.enabled ? 'enabled' : 'disabled'} for site ${variables?.siteId}`,
      })
      auditOnSuccess(data)
    }

    options?.onSuccess?.(data)
    queryClient.invalidateQueries([getSiteById.key])
    queryClient.invalidateQueries(['gqty-site-by-id'])
  }

  const onError = (error) => {
    // Track audit error
    if (featureTracker) {
      const { onError: auditOnError } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        settingName: 'site.frontDeskKioskBeta',
        settingDisplayName: 'Front Desk Kiosk Beta',
      })
      auditOnError(error)
    }

    openToast({
      title: t('settings.locationTools.toasts.betaToggleFailed', 'Beta update failed'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
    })
    options?.onError?.(error)
  }

  return useMutation(toggleFrontDeskKioskBeta, {
    onSuccess,
    onError,
    ...options,
  })
}

export const useUpdateSiteSettingsCheckIn = (options) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const { closeDialog } = useDialog()
  const queryClient = useQueryClient()
  const featureTracker = useFeatureTrackerOptional('settings.location.checkIn')

  const onSuccess = (data, variables) => {
    openToast({
      title: t('settings.checkIn.toasts.settingsUpdated', 'Check-in settings updated successfully'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
    })

    // Track audit event
    if (featureTracker) {
      const { onSuccess: auditOnSuccess } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        resourceId: variables?.siteId,
        settingName: 'site.checkIn',
        settingDisplayName: 'Check-In Settings',
        getOldValue: () => {
          const cached = queryClient.getQueryData([getSiteById.key, { siteId: variables?.siteId }])
          const checkIn = cached?.site?.settings?.location?.checkIn
          if (!checkIn) {
            return undefined
          }
          return JSON.stringify({
            enableGeneralCheckIn: checkIn.enableGeneralCheckIn,
            checkInMarksAttendance: checkIn.checkInMarksAttendance,
            enableMobileClientCheckIn: checkIn.enableMobileClientCheckIn,
            enableCheckInPopups: checkIn.enableCheckInPopups,
            enableCheckInWidget: checkIn.enableCheckInWidget,
            enableCheckInSoundAlerts: checkIn.enableCheckInSoundAlerts,
            barcodeCheckInScreenRefreshPeriodInSeconds:
              checkIn.barcodeCheckInScreenRefreshPeriodInSeconds,
            contactlessCheckIn: checkIn.contactlessCheckIn,
            bookingGateMode: cached?.site?.settings?.location?.forms?.bookingGateMode,
          })
        },
        getNewValue: () => {
          return JSON.stringify(variables?.input)
        },
        getSummary: () => `Check-in settings updated for site ${variables?.siteId}`,
      })
      auditOnSuccess(data)
    }

    options?.onSuccess?.(data)
    closeDialog()
    queryClient.invalidateQueries([getSites.key])
    queryClient.invalidateQueries([getSiteById.key])
    queryClient.invalidateQueries(['gqty-site-by-id'])
  }

  const onError = (error) => {
    // Track audit error
    if (featureTracker) {
      const { onError: auditOnError } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        settingName: 'site.checkIn',
        settingDisplayName: 'Check-In Settings',
      })
      auditOnError(error)
    }

    openToast({
      title: t('settings.checkIn.toasts.updateFailed', 'Failed to update check-in settings'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
    })
    options?.onError?.(error)
  }

  return useMutation(updateSiteSettingsCheckIn, {
    onSuccess,
    onError,
  })
}

export const useUpdateSiteSettingsSocialMedia = (options) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const { closeDialog } = useDialog()
  const queryClient = useQueryClient()
  const featureTracker = useFeatureTrackerOptional('settings.location.socialMedia')

  const onSuccess = (data, variables) => {
    openToast({
      title: t(
        'settings.socialMedia.toasts.settingsUpdated',
        'Social media settings updated successfully'
      ),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
    })

    // Track audit event
    if (featureTracker) {
      const { onSuccess: auditOnSuccess } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        resourceId: variables?.siteId,
        settingName: 'site.socialMedia',
        settingDisplayName: 'Social Media',
        getOldValue: () => {
          const cached = queryClient.getQueryData([getSiteById.key, { siteId: variables?.siteId }])
          const socialMedia = cached?.site?.settings?.location?.socialMedia
          if (!socialMedia) {
            return undefined
          }
          return JSON.stringify({
            website: socialMedia.website,
            instagram: socialMedia.instagram,
            facebook: socialMedia.facebook,
            x: socialMedia.x,
          })
        },
        getNewValue: () => {
          return JSON.stringify(variables?.input)
        },
        getSummary: () => `Social media settings updated for site ${variables?.siteId}`,
      })
      auditOnSuccess(data)
    }

    options?.onSuccess?.(data)
    closeDialog()
    queryClient.invalidateQueries([getSites.key])
    queryClient.invalidateQueries([getSiteById.key])
    queryClient.invalidateQueries(['gqty-site-by-id'])
  }

  const onError = (error) => {
    // Track audit error
    if (featureTracker) {
      const { onError: auditOnError } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        settingName: 'site.socialMedia',
        settingDisplayName: 'Social Media',
      })
      auditOnError(error)
    }

    openToast({
      title: t(
        'settings.socialMedia.toasts.updateFailed',
        'Failed to update social media settings'
      ),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
    })
    options?.onError?.(error)
  }

  return useMutation(updateSiteSettingsSocialMedia, {
    onSuccess,
    onError,
    ...options,
  })
}

export const useUpdateSiteSettingsAccountPointOfSale = (options = {}) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const { closeDialog } = useDialog()
  const queryClient = useQueryClient()
  const featureTracker = useFeatureTrackerOptional('settings.location.retail')

  const onSuccess = (data, variables) => {
    openToast({
      title: t('core:settings.account.pointOfSale.updateSuccess'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
    })

    // Track audit event
    if (featureTracker) {
      const { onSuccess: auditOnSuccess } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        resourceId: variables?.siteId,
        settingName: 'site.accountPointOfSale',
        settingDisplayName: 'Point of Sale',
        getOldValue: () => {
          const cached = queryClient.getQueryData([getSiteById.key, { siteId: variables?.siteId }])
          const pos = cached?.site?.settings?.account?.pointOfSale
          if (!pos) {
            return undefined
          }
          return JSON.stringify({
            enableCashDrawer: pos.enableCashDrawer,
            itemTypes: pos.itemTypes,
          })
        },
        getNewValue: () => {
          return JSON.stringify(variables?.input)
        },
        getSummary: () => `Point of sale settings updated for site ${variables?.siteId}`,
      })
      auditOnSuccess(data)
    }

    options?.onSuccess?.(data)
    closeDialog()
    queryClient.invalidateQueries([getSites.key])
    queryClient.invalidateQueries([getSiteById.key])
    queryClient.invalidateQueries(['gqty-site-by-id'])
  }

  const onError = (error) => {
    // Track audit error
    if (featureTracker) {
      const { onError: auditOnError } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        settingName: 'site.accountPointOfSale',
        settingDisplayName: 'Point of Sale',
      })
      auditOnError(error)
    }

    openToast({
      title: t('core:settings.account.pointOfSale.updateError'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
    })
    options?.onError?.(error)
  }

  return useMutation(updateSiteSettingsAccountPointOfSale, {
    onSuccess,
    onError,
    ...options,
  })
}

export const useSiteEnableTanningFeatures = () => {
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)

  return useQuery(
    [getSiteEnableTanningFeatures.key, { siteId }],
    getSiteEnableTanningFeatures.query,
    {
      select: (data) => data?.site,
      enabled: !!siteId,
      onError,
    }
  )
}

/** True only once the site config has loaded with tanning/wellness features enabled. */
export const useHasTanningFeatures = () => {
  const { data } = useSiteEnableTanningFeatures()
  return data?.adminConfigurations?.enableTanningFeatures === true
}

export const useCheckInSoundEnabled = () => {
  const { data: siteId, isSuccess } = useSiteId()
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)

  const query = useQuery(getConfiguration.key, getConfiguration.query, {
    select: (data) => data?.site?.configurations?.checkIn?.enableSoundAlert === true,
    enabled: isSuccess,
    meta: {
      siteId,
    },
    onError,
  })

  return query
}

export const useSiteTanningMaintenanceEmail = () => {
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)

  const query = useQuery(
    [getSiteTanningMaintenanceEmail.key, { siteId }],
    getSiteTanningMaintenanceEmail.query,
    {
      select: (data) => data?.site?.integrations?.tanning?.maintenanceEmail,
      enabled: !!siteId,
      onError,
    }
  )

  return query
}

// OC-918: whether the site's Topaz signature-pad integration is enabled — the
// checkout signing dialog combines this with the topaz-signature-pad-enabled
// feature flag before probing for a locally attached pad.
export const useSiteTopazIntegrationEnabled = () => {
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)

  return useQuery([getSiteTopazIntegration.key, { siteId }], getSiteTopazIntegration.query, {
    select: (data) => data?.site?.integrations?.topaz?.enabled ?? false,
    enabled: !!siteId,
    onError,
  })
}

export const useSiteEnableAddOns = () => {
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)

  return useQuery([getSiteEnableAddOns.key, { siteId }], getSiteEnableAddOns.query, {
    select: (data) => data?.site?.adminConfigurations?.enableAddOns ?? false,
    enabled: !!siteId,
    onError,
  })
}

export const useSiteEnableDoorAccess = () => {
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)

  return useQuery([getSiteEnableDoorAccess.key, { siteId }], getSiteEnableDoorAccess.query, {
    select: (data) => data?.site?.adminConfigurations?.enableDoorAccess ?? false,
    enabled: !!siteId,
    onError,
  })
}

/**
 * MEM-301 — the location's fob derivation config, or null when one has never been
 * defined. Null means enrollment cannot derive, so the fob field stays on manual entry.
 */
export const useSiteFobCredentialFormat = () => {
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)

  return useQuery([getSiteFobCredentialFormat.key, { siteId }], getSiteFobCredentialFormat.query, {
    select: (data) => data?.site?.adminConfigurations?.fobCredentialFormat ?? null,
    enabled: !!siteId,
    onError,
  })
}

export const useSiteAdminSmsFromName = (siteId) => {
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)

  return useQuery([getSiteAdminSmsFromName.key, { siteId }], getSiteAdminSmsFromName.query, {
    select: (data) => data?.site?.adminConfigurations?.sms?.fromName ?? null,
    enabled: !!siteId,
    onError,
  })
}

export const useSiteEnableMembershipPlanChange = () => {
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)

  return useQuery(
    [getSiteEnableMembershipPlanChange.key, { siteId }],
    getSiteEnableMembershipPlanChange.query,
    {
      select: (data) => data?.site?.adminConfigurations?.enableMembershipPlanChange ?? false,
      enabled: !!siteId,
      onError,
    }
  )
}

export const useSiteEnableSharedSites = (siteId) => {
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)

  return useQuery([getSiteEnableSharedSites.key, { siteId }], getSiteEnableSharedSites.query, {
    select: (data) => data?.site?.adminConfigurations?.enableSharedSites ?? false,
    enabled: !!siteId,
    onError,
  })
}

export const useSiteConnectedSharedSiteIds = (siteId) => {
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)

  return useQuery(
    [getSiteConnectedSharedSiteIds.key, { siteId }],
    getSiteConnectedSharedSiteIds.query,
    {
      select: (data) => data?.site?.connectedSharedSiteIds ?? [],
      enabled: !!siteId,
      onError,
    }
  )
}
